/**
 * SAH-specific consents.
 *
 * Beyond the generic consents (care / data-sharing / photos), the brokered SAH
 * model needs explicit, separately-evidenced consents because participant
 * information crosses legal entities (the registration holder + delivery
 * sub-providers) and is exchanged with Services Australia for means testing.
 * These are the SSOT consent-type tokens (mirrors the `consents.consent_type`
 * CHECK) + which are REQUIRED before SAH service delivery.
 */

export type SahConsentType =
  | 'SAH_DATA_SHARING_SUBPROVIDERS'
  | 'SAH_STATEMENTS_RECIPIENT'
  | 'SAH_MEANS_TEST_EXCHANGE';

export const SAH_CONSENT_TYPES: readonly SahConsentType[] = [
  'SAH_DATA_SHARING_SUBPROVIDERS',
  'SAH_STATEMENTS_RECIPIENT',
  'SAH_MEANS_TEST_EXCHANGE',
];

export const SAH_CONSENT_LABELS: Record<SahConsentType, string> = {
  SAH_DATA_SHARING_SUBPROVIDERS:
    'Share information with the registration holder and delivery sub-providers',
  SAH_STATEMENTS_RECIPIENT: 'Deliver monthly statements to the nominated recipient',
  SAH_MEANS_TEST_EXCHANGE: 'Means-test / Services Australia data exchange',
};

export const SAH_CONSENT_DESCRIPTIONS: Record<SahConsentType, string> = {
  SAH_DATA_SHARING_SUBPROVIDERS:
    'Required in a brokered model — the participant’s information is shared across the registration holder and each delivery sub-provider.',
  SAH_STATEMENTS_RECIPIENT:
    'Monthly statements (which contain protected information) are sent to the nominated statements recipient.',
  SAH_MEANS_TEST_EXCHANGE:
    'Income/assets information is exchanged with Services Australia to determine the participant’s contribution.',
};

/**
 * WS-0.3 — consent-gate policy (regulation-verified 2026-06-09, aged-care-legislation).
 *
 * Determination: NONE of the three SAH consents is an unconditional statutory
 * precondition to commencing Support at Home service delivery. The only
 * unconditional hard precondition is the signed service agreement (Aged Care
 * Rules 2025 r 148-70 / Act s 145), which the platform already gates. So per the
 * "block only if regulation requires, otherwise warn" ruling, these are WARN /
 * CONDITIONAL, never an unconditional onboarding-readiness block.
 *
 * - SAH_MEANS_TEST_EXCHANGE → WARN only. DoHA: the means assessment is NOT
 *   mandatory and may be done before OR after services start; non-disclosure
 *   results in the maximum contribution rate, NOT service refusal — so a hard
 *   block would itself be non-compliant.
 * - SAH_DATA_SHARING_SUBPROVIDERS → CONDITIONAL (only when a sub-provider
 *   actually delivers this participant's services); the disclosure is statutorily
 *   authorised under the Act Ch 7 (protected information), so warn, don't block.
 * - SAH_STATEMENTS_RECIPIENT → CONDITIONAL (only when a non-participant recipient
 *   is nominated); enforce at the statement-send-to-third-party step, not at
 *   onboarding-readiness.
 */
export type SahConsentGate = 'WARN' | 'CONDITIONAL';

export const SAH_CONSENT_GATE_POLICY: Record<
  SahConsentType,
  { gate: SahConsentGate; trigger?: string; basis: string }
> = {
  SAH_MEANS_TEST_EXCHANGE: {
    gate: 'WARN',
    basis:
      'Means assessment is not mandatory and may occur before or after service start; non-disclosure → maximum contribution, not service refusal (DoHA SAH guidance). A hard block would be non-compliant.',
  },
  SAH_DATA_SHARING_SUBPROVIDERS: {
    gate: 'CONDITIONAL',
    trigger: 'a delivery sub-provider handles this participant',
    basis:
      'Required when a delivery sub-provider handles this participant — the platform hard-gates sub-provider service-detail writes until this consent is recorded (Aged Care Act 2024 Ch 7 protected information).',
  },
  SAH_STATEMENTS_RECIPIENT: {
    gate: 'CONDITIONAL',
    trigger: 'monthly statements are nominated to a non-participant recipient',
    basis:
      'Required when monthly statements are nominated to a non-participant recipient — the platform hard-gates statement delivery to that recipient until this consent is recorded.',
  },
};

/**
 * DEPRECATED (WS-0.3): there are NO unconditional SAH consents that hard-block
 * onboarding-readiness or service delivery (see SAH_CONSENT_GATE_POLICY). Kept
 * as an empty export for backward-compatibility so existing readiness "complete"
 * checks stop blocking on these consents. Use SAH_RECOMMENDED_CONSENT_TYPES +
 * recommendedSahConsentsMissing() to surface them as non-blocking warnings.
 */
export const SAH_REQUIRED_CONSENT_TYPES: readonly SahConsentType[] = [];

/** All three SAH consents are RECOMMENDED (surfaced as warnings, never blockers). */
export const SAH_RECOMMENDED_CONSENT_TYPES: readonly SahConsentType[] = SAH_CONSENT_TYPES;

export function isSahConsentType(value: unknown): value is SahConsentType {
  return (SAH_CONSENT_TYPES as readonly string[]).includes(String(value ?? ''));
}

/** Is a consent record "granted"? Treats GRANTED/GIVEN/ACTIVE as granted. */
export function isConsentGranted(status: string | null | undefined): boolean {
  const s = String(status ?? '')
    .trim()
    .toUpperCase();
  return s === 'GRANTED' || s === 'GIVEN' || s === 'ACTIVE' || s === 'ACCEPTED';
}

export type SahConsentStateInput = {
  consentType: string | null | undefined;
  status: string | null | undefined;
};

/**
 * DEPRECATED (WS-0.3): no SAH consents are unconditionally required, so this now
 * returns [] — existing readiness "complete" checks that consumed it therefore
 * stop hard-blocking on SAH consents. Use recommendedSahConsentsMissing() for the
 * non-blocking warning surface. Retained so callers compile unchanged.
 */
export function missingRequiredSahConsents(
  consents: readonly SahConsentStateInput[],
): SahConsentType[] {
  const granted = new Set(
    consents.filter((c) => isConsentGranted(c.status)).map((c) => String(c.consentType ?? '')),
  );
  return SAH_REQUIRED_CONSENT_TYPES.filter((t) => !granted.has(t));
}

/**
 * The RECOMMENDED SAH consents that are not yet granted — surfaced as a
 * NON-BLOCKING warning (never a readiness/delivery block) per SAH_CONSENT_GATE_POLICY.
 * The conditional consents (data-sharing, statements-recipient) are still listed
 * as recommendations; their hard enforcement, where it applies, lives at the
 * triggering step (a sub-provider arrangement / a third-party statement send),
 * not here.
 */
export function recommendedSahConsentsMissing(
  consents: readonly SahConsentStateInput[],
): SahConsentType[] {
  const granted = new Set(
    consents.filter((c) => isConsentGranted(c.status)).map((c) => String(c.consentType ?? '')),
  );
  // Redesign compat (dual-audit fix 3 / design §2): the Tier B USE_SHARE_CARE
  // acknowledgement absorbs the former standalone sub-provider data-sharing consent.
  // Treat it as satisfying SAH_DATA_SHARING_SUBPROVIDERS so a redesign-onboarded
  // client is not warned to record a consent that no longer exists as a separate
  // token. Mirrors CARE_CONSENT_TYPES + the DB sah_client_consent_granted helper.
  if (granted.has('USE_SHARE_CARE')) {
    granted.add('SAH_DATA_SHARING_SUBPROVIDERS');
  }
  return SAH_RECOMMENDED_CONSENT_TYPES.filter((t) => !granted.has(t));
}

/** Settlement models where this tenant cannot arrange sub-provider delivery. */
const SUB_PROVIDER_CONSENT_INAPPLICABLE_SETTLEMENT_MODELS = new Set(['brokered_out']);

/** Context for whether each operational SAH consent token applies to this client. */
export type SahOperationalConsentApplicability = {
  /** Tier-B redesign acknowledgement already covers sub-provider sharing. */
  useShareCareGranted?: boolean;
  /** A delivery sub-provider / third-party line exists for this participant. */
  usesSubProviderDelivery?: boolean;
  /**
   * Sub-provider consent may still be required (brokered_in, direct_subsidy, or
   * unset settlement). False only when no brokered/sub-provider arrangement is
   * possible for this client (e.g. brokered_out).
   */
  subProviderDeliveryPossible?: boolean;
  /** Monthly statements are nominated to a non-participant representative. */
  statementsToRepresentative?: boolean;
  /** Services Australia means-test data exchange is on the table for this client. */
  meansTestExchangeRelevant?: boolean;
};

function sahConsentRowRecorded(
  consents: readonly SahConsentStateInput[],
  type: SahConsentType,
): boolean {
  return consents.some((row) => {
    if (String(row.consentType ?? '') !== type) return false;
    const status = String(row.status ?? '')
      .trim()
      .toUpperCase();
    return isConsentGranted(status) || status === 'WITHDRAWN';
  });
}

/** Whether a means-test exchange consent prompt applies (WARN gate — hide when settled). */
export function isSahMeansTestExchangeApplicable(input: {
  meansTestingStatus?: string | null;
}): boolean {
  const status = String(input.meansTestingStatus ?? '')
    .trim()
    .toLowerCase();
  return ![
    'completed',
    'declined',
    'exempt',
    'not_applicable',
    'not_required',
    'not_required_yet',
    'waived',
  ].includes(status);
}

/** Whether sub-provider data-sharing consent can still be required for this client. */
export function isSahSubProviderConsentPossible(input: {
  settlementModel?: string | null;
}): boolean {
  const model = String(input.settlementModel ?? '')
    .trim()
    .toLowerCase();
  if (!model) return true;
  return !SUB_PROVIDER_CONSENT_INAPPLICABLE_SETTLEMENT_MODELS.has(model);
}

export function deriveSahOperationalConsentApplicability(input: {
  consents: readonly SahConsentStateInput[];
  representativeReceivesStatements?: boolean | null;
  representativeEmail?: string | null;
  usesSubProviderDelivery?: boolean | null;
  settlementModel?: string | null;
  meansTestingStatus?: string | null;
}): SahOperationalConsentApplicability {
  const granted = new Set(
    input.consents
      .filter((row) => isConsentGranted(row.status))
      .map((row) => String(row.consentType ?? '')),
  );
  return {
    useShareCareGranted: granted.has('USE_SHARE_CARE'),
    usesSubProviderDelivery: input.usesSubProviderDelivery === true,
    subProviderDeliveryPossible: isSahSubProviderConsentPossible({
      settlementModel: input.settlementModel,
    }),
    // Show once a representative recipient is nominated — email may be added later
    // before the statement-send gate fires.
    statementsToRepresentative: input.representativeReceivesStatements === true,
    meansTestExchangeRelevant: isSahMeansTestExchangeApplicable({
      meansTestingStatus: input.meansTestingStatus,
    }),
  };
}

/** Should the operational SAH consent panel surface this token for recording? */
export function isSahOperationalConsentApplicable(
  type: SahConsentType,
  consents: readonly SahConsentStateInput[],
  applicability: SahOperationalConsentApplicability = {},
): boolean {
  if (sahConsentRowRecorded(consents, type)) return true;

  switch (type) {
    case 'SAH_DATA_SHARING_SUBPROVIDERS': {
      const useShareCareGranted =
        applicability.useShareCareGranted === true ||
        consents.some(
          (row) =>
            String(row.consentType ?? '') === 'USE_SHARE_CARE' && isConsentGranted(row.status),
        );
      if (useShareCareGranted) return false;
      if (applicability.usesSubProviderDelivery === true) return true;
      // Err toward showing: over-prompting is safe; hiding blocks regulated workflows.
      return applicability.subProviderDeliveryPossible !== false;
    }
    case 'SAH_STATEMENTS_RECIPIENT':
      return applicability.statementsToRepresentative === true;
    case 'SAH_MEANS_TEST_EXCHANGE':
      return applicability.meansTestExchangeRelevant !== false;
    default:
      return false;
  }
}

export function applicableSahOperationalConsentTypes(
  consents: readonly SahConsentStateInput[],
  applicability: SahOperationalConsentApplicability = {},
): SahConsentType[] {
  return SAH_CONSENT_TYPES.filter((type) =>
    isSahOperationalConsentApplicable(type, consents, applicability),
  );
}
